<template>
  <div
    class="main-product critical-component-clear"
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <div class="main-product__grid grid">
      <div class="main-product__breadcrumb-container col xs-span">
        <breadcrumbs
          :ancestor-title="liquid?.collection?.title"
          :ancestor-url="liquid?.collection?.url"
          :state="state"
          :title="productObject.title"
        />
      </div>

      <div
        id="product-gallery"
        class="main-product__gallery-column col xs-span l6"
        data-sign-post-location="main"
        js-sign-post
      >
        <product-gallery
          class="main-product__gallery"
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          layout="horizontal-vertical"
          :loading="state === 'loading'"
          :product="productObject"
          :variant="variant"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />
      </div>

      <div class="main-product__details-column col xs-span l8-11">
        <div class="main-product__details">
          <product-badges
            v-if="productPromoBadges?.length"
            class="main-product__promo-tag"
            :badges="productPromoBadges"
          />

          <h1
            id="template-title"
            class="
              main-product__title
              text-heading-xl
              text-heading-3xl-desktop
            "
            v-text="productObject.title"
          />

          <div class="main-product__price-rating">
            <div>
              <span
                class="
                  main-product__price-label
                  text-body-s-regular
                  text-body-m-regular-desktop
                "
                v-text="$string('product.prices.price_label')"
              />

              <product-prices
                class="main-product__prices-container"
                :compare-at-price="alreadyPurchasedMembership
                  ? variantObject?.price.amount
                  : variantObject?.compareAtPrice.amount
                "
                class-compare-at-price="text-body-s-regular text-body-m-regular-desktop"
                class-price="text-body-s-regular text-body-m-regular-desktop"
                :loading="state === 'loading'"
                :price="alreadyPurchasedMembership ? 0 : variantObject?.price.amount"
                :price-first="false"
                :rrp-price="product?.metafields?.market?.rrpPrice"
                show-compare-at-labels
                :varies="alreadyPurchasedMembership
                  ? false
                  : !variant && productObject?.price.varies
                "
              />

              <span
                v-if="isPersonalisationProduct"
                class="main-product__personalisation-price-addition text-body-m-regular"
                v-text="personaliseSubheader"
              />
            </div>

            <product-rating
              :product="product"
              show-cursor
              show-rating-text
              :visible="false"
              @click="handleProductRatingClick"
            />
          </div>

          <span
            v-if="variant?.sku"
            class="main-product__product-code text-body-s-regular"
            v-text="`${$string('product.product_code')}: ${variant?.sku}`"
          />

          <span
            v-if="
              productObject?.metafields?.launchDate && !isPreOrderSoldOut
            "
            class="main-product__product_launch_date  text-body-s-regular"
            v-text="
              `${$string('product.launch_date')} ${$formatDate(
                'date_formats.day_abbreviated_month_year',
                new Date(productObject?.metafields?.launchDate)
              )} `
            "
          />
        </div>

        <div ref="appBlocksHolder" />

        <product-form
          class="main-product__form"
          :initial-quantity="sharedQuantity"
          :loading="state === 'loading'"
          :personalisation-guides="personalisationGuides"
          :product="productObject"
          :section-id="sectionId"
          :settings="settings"
          :variant="variant"
          :already-purchased-membership="alreadyPurchasedMembership"
          :one-per-order-enabled="onePerOrderEnabled"
          @update-quantity="handleQuantityUpdate"
          @update-variant="handleVariantUpdate"
        />

        <div
          v-if="product.giftCard && settings.giftCardMessage"
          class="main-product__gift-card-message-wrapper"
        >
          <div class="main-product__gift-card-message">
            <icon-info />

            <p
              class="main-product__gift-card-message-text text-body-xs-regular"
              v-text="$string('product.form.gift_card_message')"
            />
          </div>
        </div>

        <pre-order-message
          :product="product"
          :variant="variant"
        />

        <div
          v-if="!isCollectorsClubRestricted && !alreadyPurchasedMembership"
          class="main-product__delivery-stock"
        >
          <div
            v-if="onePerOrderEnabled"
            class="main-product__one-per-order-info"
          >
            <icon-info />

            <span
              class="text-body-xs-regular"
              v-text="$string(
                'product.form.one_per_order_info',
                {
                  replace: { max_quantity: productObject?.limits?.perCustomer },
                },
              )"
            />
          </div>

          <div
            v-else-if="availabilityMessage?.length"
            class="main-product__availability-message"
          >
            <icon-delivery />

            <span
              class="text-body-xs-regular"
              v-text="`${$string('product.availability_message_prefix')} ${availabilityMessage}`"
            />
          </div>

          <free-shipping-indicator
            v-else-if="freeShippingThreshold > 0"
            class="main-product__free-shipping"
            :free-shipping-threshold="freeShippingThreshold"
          />

          <product-stock-message
            class="main-product__low-stock"
            :enabled="settings.lowStockMessagingEnabled"
            :threshold="settings.lowStockThreshold"
            :product="productObject"
            :variant="variant"
          />
        </div>

        <a
          v-if="productObject.personalisationCrossSell && settings.personalisationFeatureEnabled"
          class="main-product__personalisation-cross-sell"
          :href="personalisationCrossSellUrl"
        >
          <span
            class="text-other-label-s"
            v-text="$string('product.personalisation_cross_sell')"
          />
          <icon-chevron-right />
        </a>

        <!-- Product Details -->
        <product-details
          class="main-product__product-details"
          :product="productObject"
          :variant="variantObject"
          :additional-content="additionalContent"
          :delivery-content="deliveryContent"
        />
      </div>
    </div>

    <!-- Sticky Add to Basket Bar -->
    <Teleport to="body">
      <transition name="sticky-bar-slide">
        <div
          v-if="showStickyBar && !factoryTicket"
          class="main-product__sticky-bar"
          role="complementary"
          :aria-label="$string('product.sticky_bar_label')"
        >
          <div class="main-product__sticky-bar-inner">
            <div class="main-product__sticky-bar-product">
              <responsive-image
                v-if="productObject.featuredImage"
                class="main-product__sticky-bar-image"
                :image="productObject.featuredImage"
                :padding="false"
                ratio="1:1"
                sizes="60px"
              />

              <div class="main-product__sticky-bar-info">
                <p
                  class="main-product__sticky-bar-title text-heading-2xl text-heading-2xl-desktop"
                  v-text="productObject.title"
                />

                <product-prices
                  class="main-product__sticky-bar-prices"
                  :compare-at-price="alreadyPurchasedMembership
                    ? variantObject?.price.amount
                    : variantObject?.compareAtPrice.amount
                  "
                  class-compare-at-price="text-body-m-bold"
                  class-price="text-body-m-bold"
                  :loading="state === 'loading'"
                  :price="alreadyPurchasedMembership ? 0 : variantObject?.price.amount"
                  :price-first="false"
                  show-compare-at-labels
                  :varies="alreadyPurchasedMembership
                    ? false
                    : !variant && productObject?.price.varies
                  "
                />
              </div>
            </div>

            <div class="main-product__sticky-bar-actions">
              <product-form
                class="main-product__sticky-bar-form"
                :initial-quantity="sharedQuantity"
                :loading="state === 'loading'"
                :product="productObject"
                :section-id="`${sectionId}-sticky`"
                :settings="stickyBarSettings"
                :variant="variant"
                :already-purchased-membership="alreadyPurchasedMembership"
                :one-per-order-enabled="onePerOrderEnabled"
                @update-quantity="handleQuantityUpdate"
                @update-variant="handleVariantUpdate"
              />
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>

/**
 * Vue: Main product (main-product)
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {String} [additionalContent] - Additional content for description
 * accordion.
 * @param {String} [deliveryContent] - Content for the delivery accordion
 * @param {Number} [freeShippingThreshold] - Free shipping threshold amount.
 * @param {Boolean|String} [handle] - Product handle to load (Storybook).
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Array} personalisationGuides - Array of personalisation guides
 * containing personalisation placements and images.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {String} sectionId - Section ID.
 * @param {Object} settings - Product form settings.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 * @emits updateRequestObject - Emit new product object.
 *
 */

import { mapGetters, mapState } from 'vuex'
import Breadcrumbs from '~async/breadcrumbs/breadcrumbs'
import FreeShippingIndicator from '~async/free-shipping-indicator/free-shipping-indicator'
import ProductDetails from '~async/product-details/product-details'
import ProductForm from '~async/product-form/product-form'
import ProductGallery from '~async/product-gallery/product-gallery'
import ProductPrices from '~global/product-prices/product-prices'
import ProductStockMessage from '~async/product-stock-message/product-stock-message'
import ProductRating from '~async/product-rating/product-rating'
import ProductBadges from '~async/product-badges/product-badges'
import ResponsiveImage from '~global/images/responsive-image'
import IconDelivery from '~icons/general/delivery.svg'
import IconChevronRight from '~icons/directional-navigation/chevron/right.svg'
import IconInfo from '~icons/general/info.svg'
import PreOrderMessage from '~async/pre-order-message/pre-order-message'

import classes from '~/config/classes'
import productQuery from '~graphql/product.gql'
import { formatProduct } from '~/format/format-product'
import { emitUpdateRequestObject } from '~/helpers/vue'

export default {
  name: 'MainProduct',

  components: {
    Breadcrumbs,
    FreeShippingIndicator,
    ProductDetails,
    ProductForm,
    ProductGallery,
    ProductPrices,
    ProductStockMessage,
    IconDelivery,
    IconChevronRight,
    IconInfo,
    ProductBadges,
    ProductRating,
    PreOrderMessage,
    ResponsiveImage,
  },

  props: {
    additionalContent: {
      type: String,
      default: '',
    },
    deliveryContent: {
      type: String,
      default: '',
    },
    factoryTicket: {
      type: String,
      default: '',
    },
    freeShippingThreshold: {
      type: Number,
      default: 0,
    },
    handle: {
      type: [Boolean, String],
      default: false,
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    personalisationGuides: {
      type: Array,
      default: () => [],
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    sectionId: {
      type: String,
      default: '',
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      controller: false,
      currentIndex: 0,
      currentNamespace: 'gallery',
      sharedQuantity: 1,
      showStickyBar: false,
      state: 'loading',
      selectors: {
        appBlocks: '[js-section="mainProductSection"] .shopify-app-block',
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      isCollectorsClubRestricted: 'collectorsClub/getProductCollectorsClubRestricted',
    }),

    ...mapState({
      collectorClubMembershipProduct: (state) => state.collectorsClub.membershipProduct,
      isCollectorsClubMember: (state) => state.collectorsClub.customer.isCollector,
    }),

    /**
     * Compute if the current product is the Collectors Club Membership product
     * and the user is already a collector
     * @returns {Boolean}
     */
    alreadyPurchasedMembership() {
      return this.isCollectorsClubMember && this.productIsMembershipProduct
    },

    /**
     * Compute if the current product is the Collectors Club Membership product
     * - Checks the current product id against the one in the collectors club
     *   state.
     * @returns {Boolean}
     */
    productIsMembershipProduct() {
      return this.productObject?.id &&
        this.productObject?.id === this.collectorClubMembershipProduct?.id
    },

    /**
     * Compute if the current product is a personalisation product.
     * @returns {Boolean}
     */
    isPersonalisationProduct() {
      return Boolean(this.productObject?.personalisation?.enabled)
    },

    /**
     * Compute price per letter of personalised products.
     * - Subtracts price of double letter variant from single letter variant
     * - Returns string formatted with store currency
     * @returns {String}
     */
    pricePerLetter() {
      const singleLetter = this.productObject.variants.find(variant => variant.title === '1')
      const doubleLetter = this.productObject.variants.find(variant => variant.title === '2')

      if (!singleLetter || !doubleLetter) {
        return this.$formatMoney(100, { removeZeros: true })
      }

      const difference = doubleLetter.price.amount - singleLetter.price.amount
      return this.$formatMoney(difference, { removeZeros: true })
    },

    /**
     * Compute local handle from prop or page handle.
     * @returns {Boolean|String}
     */
    localHandle() {
      if (this.$variable('storybook') && this.handle === false) {
        return false
      }

      return this.handle || this.$variable('page.handle')
    },

    /**
     * Compute personalisation cross sell product URL.
     * @returns {String}
     */
    personalisationCrossSellUrl() {
      const crossSellHandle = `personalised-${this.product.handle}`
      return `${cnvs.routes.normalisedRoot}products/${crossSellHandle}`
    },

    /**
     * Compute product object.
     * @returns {Object}
     */
    productObject() {
      return this.product || this.liquid
    },

    /**
     * Compute variant object.
     * @returns {Object}
     */
    variantObject() {
      if (this.variant) {
        return this.variant
      }

      /**
       * If it is a personalisation product, ignore any selected variant from
       * the URL and fallback to the cheapest variant instead.
       * - Otherwise things like price show the price of the currently selected
       *   variant without the matching messaging in the personalisation form.
       */
      if (this.isPersonalisationProduct) {
        const cheapestVariant = [...this.productObject.variants].sort((variantA, variantB) => {
          return variantA.price.amount - variantB.price.amount
        })[0]

        return cheapestVariant
      }

      return this.productObject.selectedOrFirstAvailableVariant
    },

    /**
     * Compute vendor collection URL.
     * @returns {String}
     */
    vendorCollectionUrl() {
      return this.productObject.vendor
        ? `${this.$variable('routes.normalisedRoot')}collections/vendors?q=${encodeURI(this.productObject.vendor)}`
        : ''
    },

    /**
     * Compute promo badge to display above product title.
     * - If collectors exclusive product, show that as a badge.
     * - Otherwise check the `promoTag` metafield.
     * - Returns array as that is required by the <product-badges> component.
     * @returns {Array} - Array of badges to display.
     */
    productPromoBadges() {
      if (this.alreadyPurchasedMembership) {
        return [this.$string('product.collectors_member_promo_tag')]
      }

      if (this.productObject?.limits?.collectors) {
        return [this.$string('product.collectors_exclusive_promo_tag')]
      }

      if (this.productObject?.limits?.collectorsEarly) {
        return [this.$string('product.collectors_early_promo_tag')]
      }

      if (this.productObject?.promoTag) {
        return [this.productObject.promoTag]
      }

      return []
    },

    /*
     * Compute personalise subheader.
     * @returns {String}
     */
    personaliseSubheader() {
      return this.variantObject.title === '0'
        ? this.$string(
          'product.personalisation.personalise_subheader',
          {
            replace: { amount: this.pricePerLetter },
          },
        )
        : this.$string(
          'product.personalisation.personalise_subheader_inc',
          {
            replace: { amount: this.pricePerLetter },
          },
        )
    },

    /**
     * Compute to check if one per order enabled.
     * @returns {Boolean}
     */
    onePerOrderEnabled() {
      return this.productObject?.limits?.onePerOrder
    },

    /**
     * Compute settings for sticky bar product form.
     * - Hides product options and shows only quantity + add to cart.
     * @returns {Object}
     */
    stickyBarSettings() {
      return {
        ...this.settings,
        displaySingleOptionNames: false,
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        displayQuantitySelector: true,
      }
    },

    /**
     * Compute availability message.
     * @returns {String}
     */
    availabilityMessage() {
      let availabilityMessageString = ''
      const availabilityMessageTag = this.product.tags?.some((tag) => tag.includes('Availability:'))

      if (availabilityMessageTag) {
        this.product.tags?.find((tag) => {
          availabilityMessageString = tag.split(':')[1]

          return availabilityMessageString
        })
      }

      return availabilityMessageString
    },

    /**
     * Compute if pre-order is sold out.
     * - Hides launch date only when pre-order product is sold out.
     * @returns {Boolean}
     */
    isPreOrderSoldOut() {
      const hasPreOrderTag = this.productObject?.tags?.some((tag) => tag.includes('trade-pre-order'))
      return hasPreOrderTag && !this.variantObject?.available
    },
  },

  watch: {

    /**
     * Watch handle for update to load new product.
     */
    handle() {
      this.queryProduct(this.localHandle)
    },

    /**
     * Watch sticky bar visibility to disable pointer-events on Trustpilot
     * iframes. Prevents the iframe from intercepting clicks on the sticky bar
     * in desktop responsive/mobile mode.
    */
    showStickyBar(visible) {
      const iframes = document.querySelectorAll('.product-trustpilot-section iframe')

      iframes.forEach((iframe) => {
        iframe.style.pointerEvents = visible ? 'none' : ''
      })
    },
  },

  mounted() {
    this.setProduct()
    this.moveAsyncAllElements(
      this.selectors.appBlocks,
      this.$refs.appBlocksHolder,
    )
    this.initStickyBarObserver()
  },

  beforeUnmount() {
    this.destroyStickyBarObserver()
  },

  methods: {

    /**
     * Set Liquid product object to product.
     */
    setProduct() {
      if (
        !this.liquid.handle ||
        (this.$variable('storybook') && this.handle === false)
      ) {
        this.queryProduct()
        return
      }

      this.$emit('update-request-object', {
        response: this.liquid,
        type: 'product',
      })

      this.state = 'ready'
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.localHandle) {
      if (this.controller.abort) {
        this.controller.abort()
      }

      if (handle === false || this.product.handle === handle) {
        return
      }

      try {
        this.state = 'loading'
        this.controller = new AbortController()

        const response = await cnvs.Query({
          query: productQuery,
          signal: this.controller.signal,
          variables: {
            country: this.$variable('store.country'),
            handle,
            language: this.$variable('store.language'),
          },
        })

        if (this.$variable('settings.disableComponentReadyState')) {
          return
        }

        const formattedProduct = formatProduct(response)

        this.$emit('update-request-object', {
          response: formattedProduct,
          type: 'product',
        })

        this.updateProductUrl(formattedProduct.handle)
        this.state = 'ready'

      } catch (error) {
        if (error.name === 'AbortError') {
          return
        }

        cnvs.ReportError('Failed to load product', error)
      }
    },

    /**
     * Update product URL when loading new handle.
     * - Replaces state so it doesn't affect back navigation.
     * - Use pushState instead to affect back navigation.
     * - Maintains /collections/ URL.
     * @param {String} handle - New product handle.
     */
    updateProductUrl(handle) {
      const oldHandle = location.pathname.split('/').reverse()[0]

      if (oldHandle === handle) {
        return
      }

      const pathname = location.pathname.replace(oldHandle, handle)
      const url = `${location.origin}${pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Update current carousel index.
     * @param {Number} newIndex - New carousel index.
     */
    handleUpdateCurrentIndex(newIndex) {
      if (newIndex === this.currentIndex) {
        return
      }

      this.currentIndex = newIndex
    },

    /**
     * Update current carousel namespace.
     * @param {String} newNamespace - New carousel namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.currentNamespace = newNamespace
    },

    /**
     * Handle variant update from product form.
     * @param {Boolean|Null|Object} variant - Emitted variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      /**
       * Skip URL update for personisation product.
       */
      if (this.isPersonalisationProduct) {
        return
      }

      const params = new URLSearchParams(document.location.search.substring(1))

      if (variant?.id) {
        params.set('variant', variant.id)
      }

      const url = variant?.id
        ? `${location.origin}${location.pathname}?${params.toString()}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Initialise IntersectionObserver for sticky bar visibility.
     * - Shows the sticky bar when the product form scrolls out of view.
     */
    async initStickyBarObserver() {
      await this.$nextTick()
      const productForm = this.$el.querySelector('.main-product__form')

      if (!productForm) {
        return
      }

      this.stickyBarObserver = new IntersectionObserver(
        ([entry]) => {
          this.showStickyBar = !entry.isIntersecting
        },
        { threshold: 0 },
      )

      this.stickyBarObserver.observe(productForm)
    },

    /**
     * Destroy IntersectionObserver for sticky bar.
     */
    destroyStickyBarObserver() {
      if (this.stickyBarObserver) {
        this.stickyBarObserver.disconnect()
        this.stickyBarObserver = null
      }
    },

    /**
     * Scroll to the product form when personalise button is clicked.
     */
    scrollToProductForm() {
      const productForm = this.$el.querySelector('.main-product__form')

      if (!productForm) {
        return
      }

      productForm.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    },

    /**
     * Handle quantity update from either product form.
     * @param {Number} quantity - Updated quantity value.
     */
    handleQuantityUpdate(quantity) {
      this.sharedQuantity = quantity
    },

    /**
     * Handle click on product rating.
     * - Scrolls main trustpilot section into view if found.
     * @param {Event} event click event
     */
    handleProductRatingClick(event) {
      const isRatingReady = event.target?.classList.contains('is-ready')
      if (!isRatingReady) {
        return
      }

      const trustpilotSection = document.querySelector('.product-trustpilot-section .trustpilot-widget')

      if (!trustpilotSection) {
        return
      }

      trustpilotSection.scrollIntoView({
        behavior: 'smooth',
      })
    },

    /**
     * Move asynchronous all elements after it's rendered.
     * @param {String} selector - Element selector.
     * @param {HTMLElement} target - Target destination.
     */
    moveAsyncAllElements(selector, target) {
      const moveInterval = setInterval(() => {
        const containers = document.querySelectorAll(selector)

        if (containers.length <= 0) {
          return
        }

        clearInterval(moveInterval)

        containers.forEach(element => {
          target.appendChild(element)
          element.classList.remove(classes.hidden)
        })
      }, 500)
    },
  },
}
</script>

<style lang="scss">
@import '~async/product-gallery-modal/product-gallery-modal-overlay';
@import './main-product';
</style>
